import { boundingBox } from '../../common/pdfReport';

/**
 * Describes a Shore-S analysis as a document.
 * Shore-S is not a measurement of the past but a comparison: a modelled coastline against a
 * reference one. So its report is built around the error between the two, and the one thing it has
 * to make unmistakable is which number means what - an RMSE over every paired observation, one over
 * the last six months, and one over the final date alone are three different claims about the same
 * model, and quoting the wrong one overstates or understates the fit.
 */

/**
 * @constant REPORT_MODULE
 * @description Module key, used to name the file and to register the export.
 * @memberof module:ShoreS
 */
export const REPORT_MODULE = 'shore_s';

/**
 * @constant ERROR_FIELDS
 * @description The error metrics, in the order they are reported, paired with the wording key each
 * one is described by. These are the names {@link computeErrorMetrics} writes plus the measured
 * deviation and the skill score the saga adds to them.
 * @memberof module:ShoreS
 */
export const ERROR_FIELDS = [
  ['rmseAvg', 'rmseAvg'],
  ['biasAvg', 'biasAvg'],
  ['rmseLast6m', 'rmseLast6m'],
  ['rmseFinal', 'rmseFinal'],
  ['biasFinal', 'biasFinal'],
  ['rmsm', 'rmsm'],
  ['biasm', 'biasm'],
  ['bss', 'bss'],
];

/**
 * @function errorRows
 * @description The error metrics as report rows, each labelled with what it was measured over.
 * A metric that could not be computed - a window with no observations in it - is left out rather
 * than written as `NaN`, which a reader would take for a number.
 * @param {Object} [metrics] - `errorMetrics` from the analysis.
 * @param {Object} text - Translated names, keyed as in {@link ERROR_FIELDS}.
 * @memberof module:ShoreS
 * @returns {Array<Array<String>>}
 */
export const errorRows = (metrics, text) =>
  ERROR_FIELDS.map(([field, key]) => {
    const value = Number(metrics?.[field]);
    return Number.isFinite(value) ? [text[key], value.toFixed(3)] : null;
  }).filter(Boolean);

/**
 * @function perDateTable
 * @description The error over time, one row per reference date, which is what shows whether the
 * model drifts as it runs forward rather than being wrong evenly throughout.
 * @param {Array<Object>} [byDate] - `{ time, bias, rms }` entries.
 * @param {Object} text - `{ columns: { date, bias, rms } }`, already translated.
 * @memberof module:ShoreS
 * @returns {Object} A table for {@link renderReport}.
 */
export const perDateTable = (byDate, text) => ({
  columns: [text.columns.date, text.columns.rms, text.columns.bias],
  rows: (byDate || [])
    .slice()
    .sort((a, b) => new Date(a.time) - new Date(b.time))
    .map((entry) => {
      const when = new Date(entry.time);
      return [
        Number.isNaN(when.getTime()) ? String(entry.time) : when.toISOString().slice(0, 10),
        Number.isFinite(Number(entry.rms)) ? Number(entry.rms).toFixed(2) : null,
        Number.isFinite(Number(entry.bias)) ? Number(entry.bias).toFixed(2) : null,
      ];
    }),
});

/**
 * @function perTransectTable
 * @description The error per transect, which localises a poor fit to a stretch of coast instead of
 * spreading it over the whole analysis.
 * @param {Array<Object>} [byTransect] - `{ transectId, bias, rms }` entries.
 * @param {Object} text - `{ columns: { transect, bias, rms } }`, already translated.
 * @memberof module:ShoreS
 * @returns {Object} A table for {@link renderReport}.
 */
export const perTransectTable = (byTransect, text) => ({
  columns: [text.columns.transect, text.columns.rms, text.columns.bias],
  rows: (byTransect || [])
    .slice()
    .sort((a, b) => Number(a.transectId) - Number(b.transectId))
    .map((entry) => [
      entry.transectId,
      Number.isFinite(Number(entry.rms)) ? Number(entry.rms).toFixed(2) : null,
      Number.isFinite(Number(entry.bias)) ? Number(entry.bias).toFixed(2) : null,
    ]),
});

/**
 * @function buildShoreSReport
 * @description The whole document, as data.
 * @param {Object} params
 * @param {Array<Array<Number>>} [params.baselineCoords] - Baseline vertices.
 * @param {String} [params.crs] - The CRS the reference coastlines came in.
 * @param {Object} [params.analysis] - `state.shoreSAnalysis`, holding `errorMetrics`,
 *  `aggregatedMetrics` and `mergedDistances`.
 * @param {Object} [params.modeledCoastlines] - The modelled coastlines, as GeoJSON or a list.
 * @param {Object} [params.referenceCoastlines] - The reference coastlines, same.
 * @param {Array<Object>} [params.images] - Already loaded by {@link loadImageForReport}.
 * @param {Object} params.text - Every piece of wording, already translated.
 * **Called by:
 * @see {ProcessingPage} - Shore-S's result page.
 * @memberof module:ShoreS
 * @returns {Object} A document description for {@link renderReport}.
 */
export const buildShoreSReport = ({
  baselineCoords,
  crs,
  analysis,
  modeledCoastlines,
  referenceCoastlines,
  images,
  text,
}) => {
  /**
   * The coastlines reach here as a FeatureCollection from one path and as a plain list from
   * another, so the count is taken from whichever of the two this is.
   */
  const count = (coastlines) => coastlines?.features?.length ?? coastlines?.length ?? null;

  return {
    title: text.title,
    subtitle: text.subtitle,
    footer: text.footer,
    missingImageNote: text.missingImage,
    sections: [
      {
        heading: text.areaHeading,
        rows: [
          [text.baselineVertices, baselineCoords?.length],
          [text.baselineExtent, boundingBox(baselineCoords)],
          [text.crs, crs],
          [text.modeledCount, count(modeledCoastlines)],
          [text.referenceCount, count(referenceCoastlines)],
          [text.pairedObservations, analysis?.mergedDistances?.length],
        ],
      },
      {
        heading: text.errorHeading,
        rows: errorRows(analysis?.errorMetrics, text.errors),
        note: text.errorNote,
      },
      {
        heading: text.byDateHeading,
        table: perDateTable(analysis?.aggregatedMetrics?.byDate, text.byDate),
        note: analysis?.aggregatedMetrics?.byDate?.length ? text.byDateNote : null,
      },
      {
        heading: text.byTransectHeading,
        table: perTransectTable(analysis?.aggregatedMetrics?.byTransect, text.byTransect),
        note: analysis?.aggregatedMetrics?.byTransect?.length ? text.byTransectNote : null,
      },
      { heading: text.mapsHeading, images: images || [] },
    ],
  };
};
